import { Location } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AppSettings } from 'src/app/app-settings';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { CropperImageComponent } from 'src/app/components/bloques/cropper-image/cropper-image.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CiudadesService } from 'src/app/services/ciudades.service';
import { GenerateMapService } from 'src/app/services/generate-map.service';
import { GooglePlacesService } from 'src/app/services/google-places.service';
import { PlacesService } from 'src/app/services/places.service';
import { of, Subscription, switchMap } from 'rxjs';
import { skip } from 'rxjs/operators';

@Component({
  selector: 'app-create-venue',
  templateUrl: './create-venue.component.html',
  styleUrls: ['./create-venue.component.scss'],
})
export class CreateVenueComponent {
  venueForm!: FormGroup;
  imgControl: FormControl = new FormControl(null);
  changedImg: boolean = false;

  business = JSON.parse(localStorage.getItem('business') || '{}');
  lugar: any = '';
  searchResults: any[] = [];
  openedCreateMap: boolean = false;
  allowSuperAdmin: boolean = false;
  payloadExtras: any = {};
  loading: boolean = false;
  private mapSub!: Subscription;

  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

  constructor(
    private placeService: PlacesService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private googlePlacesService: GooglePlacesService,
    private ciudadService: CiudadesService,
    private location: Location,
    private generateMapService: GenerateMapService,
    private auth: AuthenticationService,
  ) {
    // Detectar si es superadmin
    const profile = this.auth.getProfile();
    const rolesProfile = profile?.roles.map((role: any) => role.name) || [];
    this.allowSuperAdmin = rolesProfile.includes('superadmin');
    this.venueForm = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', [Validators.required]],
      file: ['', [Validators.required]],
      id_city: [null],
      address: ['', Validators.required],
      latitude: ['', Validators.required],
      longitude: ['', Validators.required],
      capacity: ['', Validators.required],
      slug: [null, Validators.required],
      active: [true, Validators.required],
      allow_subscription: [false],
      membership_title: [],
      subscription_text: [],
      requires_accepting: [true, Validators.required],
      general_conditions: [null],
    });
  }

  ngAfterViewInit(): void {
    this.googlePlacesService.openMap();

    this.lugar = '';

    this.mapSub = this.googlePlacesService.mapsLocatorStorage.pipe(skip(1)).subscribe({
      next: (data) => {
        if (data && data.address_components) {
          const components = data.address_components;
          const city = this.getComponent(components, 'locality');

          if (!city) {
            this.openInfoCard(
              'La localización debe ser más específica (selecciona una ciudad o dirección exacta).',
            );
            this.lugar = '';
            this.venueForm.patchValue({
              latitude: '',
              longitude: '',
              address: '',
              id_city: null,
            });
            this.payloadExtras = {};
            return;
          }

          this.lugar = data;

          this.venueForm.controls['latitude'].setValue(
            data.geometry?.location.lat(),
          );
          this.venueForm.controls['longitude'].setValue(
            data.geometry?.location.lng(),
          );
          this.venueForm.controls['address'].setValue(data.formatted_address);

          const province = this.getComponent(
            components,
            'administrative_area_level_2',
          );
          const country = this.getComponent(components, 'country');
          const postalCode = this.getComponent(components, 'postal_code');
          const countryCode = this.getShortComponent(components, 'country');

          if (city) {
            this.ciudadService.getAllCitieByName(city).subscribe((res) => {
              if (res.data?.length > 0) {
                let firstCity = res.data[0];
                this.venueForm.controls['id_city'].setValue(firstCity.id);
              } else if (res.data?.length === 0) {
                this.ciudadService
                  .getAllCitieByName(this.business.city)
                  .subscribe((res) => {
                    if (res.data?.length > 0) {
                      let firstCity = res.data[0];
                      this.venueForm.controls['id_city'].setValue(firstCity.id);
                    }
                  });
              }
            });
          }

          this.payloadExtras = {
            city: city,
            province: province,
            country: country,
            country_code: countryCode,
            zip_code: postalCode,
          };
        }
      },
    });
  }

  ngOnDestroy(): void {
    if (this.mapSub) {
      this.mapSub.unsubscribe();
    }

    this.lugar = '';
    this.payloadExtras = {};
    this.venueForm.patchValue({
      latitude: '',
      longitude: '',
      address: '',
      id_city: null,
    });
  }

  // Función auxiliar para buscar en el array de Google
  private getComponent(components: any[], type: string): string {
    const res = components.find((c) => c.types.includes(type));
    return res ? res.long_name : '';
  }

  private getShortComponent(components: any[], type: string): string {
    const res = components.find((c) => c.types.includes(type));
    return res ? res.short_name : ''; // Cambiamos long_name por short_name
  }

  onFileChange(event: any) {
    if (event.target.files && event.target.files.length) {
      const file = event.target.files[0];

      // Validar que sea una imagen
      if (!file.type.match('image.*')) {
        this.openInfoCard('Solo se permite utilizar imágenes', false, true);
        event.target.value = '';
        return;
      }

      // Validar tamaño máximo de 10 MB (10485760 bytes)
      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (file.size > maxSize) {
        this.openInfoCard(
          'La imagen no puede ocupar <br> más de 10 MB.',
          false,
          true,
        );
        event.target.value = '';
        return;
      }

      const dialogRef = this.dialog.open(CropperImageComponent, {
        panelClass: 'info-dialog',
        disableClose: true,
        data: event,
      });

      dialogRef.afterClosed().subscribe((result) => {
        event.target.value = '';
        if (result) {
          this.imgControl.setValue(result.pathUrl);
          this.venueForm.patchValue({
            file: result.object.base64,
          });
          this.changedImg = true;
        }
      });
    }
    if (!event.target.files) {
      this.imgControl.setValue(null);
      this.venueForm.patchValue({
        file: null,
      });
      this.changedImg = false;
    }
  }

  onNameChange() {
    const name = this.venueForm.controls['name'].value as string;
    const currentSlug = this.venueForm.controls['slug'].value as string | null;

    if (!name) {
      this.venueForm.controls['slug'].setValue(null);
      return;
    }

    // Solo autogeneramos el slug si está vacío, para no pisar cambios manuales
    if (!currentSlug) {
      const baseSlug = name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '');

      const randomNumber = Math.floor(1000 + Math.random() * 9000); // 4 dígitos
      const finalSlug = baseSlug ? `${baseSlug}-${randomNumber}` : `${randomNumber}`;

      this.venueForm.controls['slug'].setValue(finalSlug);
    }
  }

  cleanSlug() {
    const slug = this.venueForm.controls['slug'].value as string;
    if (slug) {
      this.venueForm.controls['slug'].setValue(slug.replace(/\s+/g, ''), { emitEvent: false });
    }
  }

  updateFile() {}

  onSearchPlaces() {
    this.venueForm.patchValue({
      latitude: '',
      longitude: '',
      address: '',
      id_city: null,
    });
    this.payloadExtras = {};

    if (this.lugar) {
      this.googlePlacesService.searchPlaces(this.lugar).then((results) => {
        this.searchResults = results;
      });
    } else {
      this.searchResults = [];
    }
  }

  onBlurAddress() {
    setTimeout(() => {
      if (typeof this.lugar === 'string' && this.lugar.trim() !== '') {
        if (this.autocomplete) {
          this.autocomplete.closePanel();
        }
        this.openInfoCard(
          'Debes seleccionar una dirección de las opciones sugeridas por el buscador.',
        );
        this.lugar = '';
        this.searchResults = [];
        this.venueForm.patchValue({
          latitude: '',
          longitude: '',
          address: '',
          id_city: null,
        });
        this.payloadExtras = {};
      }
    }, 250);
  }

  onSelectPlace(text: string) {
    this.googlePlacesService.searchPlaces(text).then((results) => {
      this.lugar = results[0];
      this.googlePlacesService.getDetail(results[0]);
    });
  }

  openInfoCard(
    msg: string,
    disabledClose: boolean = false,
    icon: boolean = true,
    close?: boolean,
  ) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false,
      },
    });
    if (close) {
      dialogRef.afterClosed().subscribe({
        next: (res) => {
          this.location.back();
        },
      });
    }
  }

  displayFn(location: any): string {
    if (location) {
      return location.description
        ? location.description
        : location.formatted_address;
    }
    return '';
  }

  getSavedMap() {
    const savedMap = this.generateMapService.generatePayloadMap();
    const map_configuration = savedMap.configuration;
    const map_sections = savedMap.sections;
    const map_levels = savedMap.levels;
    const map_seats = savedMap.seats;
    const map_blocks = savedMap.blockedSeats;

    // Añadir los valores al formulario
    this.venueForm.controls['map_configuration'].setValue([map_configuration]);
    this.venueForm.controls['map_sections'].setValue(map_sections);
    this.venueForm.controls['map_levels'].setValue(map_levels);
    this.venueForm.controls['map_seats'].setValue(map_seats);
    this.venueForm.controls['map_nav_preview'].setValue(savedMap.navPreview);
    this.venueForm.controls['zone_navigation'].setValue(savedMap.zoneNavigation);
    this.venueForm.controls['capacity'].setValue(null);
  }

  saveVenue() {
    if (!this.venueForm.valid) {
      this.venueForm.markAllAsTouched();

      // Mensaje específico si la dirección está escrita pero no seleccionada del listado
      if (
        !this.venueForm.get('latitude')?.value &&
        typeof this.lugar === 'string' &&
        this.lugar?.trim() !== ''
      ) {
        this.openInfoCard(
          'Debes seleccionar obligatoriamente una localización de la lista sugerida.',
        );
      } else {
        this.openInfoCard('Por favor, completa los campos obligatorios.');
      }
      return;
    }

    this.loading = true;
    const currentCityId = this.venueForm.get('id_city').value;

    const prepareCity$ = currentCityId
      ? of({ id_city: currentCityId })
      : this.ciudadService.createAndGetCity({
          city: this.payloadExtras.city,
          province: this.payloadExtras.province,
          country: this.payloadExtras.country,
          country_code: this.payloadExtras.country_code,
        });

    prepareCity$
      .pipe(
        switchMap((cityRes: any) => {
          // Usamos el ID de la respuesta (ej: 21)
          const finalCityId = cityRes?.id_city;

          const venuePayload = {
            ...this.venueForm.value,
            id_company: this.business.id,
            id_city: finalCityId,
            ...this.payloadExtras,
          };
          return this.placeService.createVenue(venuePayload);
        }),
      )
      .subscribe({
        next: (value) => {
          this.loading = false;
          this.openInfoCard('Espacio creado correctamente', true, false, true);
        },
        error: (err) => {
          this.loading = false;
          const errors = err?.error?.errors;

          if (errors?.city || errors?.province) {
            this.openInfoCard(
              'La localización debe ser más específica (selecciona una ciudad o dirección exacta).',
              false,
              true,
            );
          } else if (errors?.slug && errors.slug.length > 0) {
            this.openInfoCard(
              'El nombre del espacio ya esta en uso',
              false,
              true,
            );
          } else if (err?.error?.message) {
            this.openInfoCard(err.error.message, false, true);
          } else {
            this.openInfoCard(
              'Error al procesar la ciudad o el espacio.',
              false,
              true,
            );
          }
        },
      });
  }
  createMap() {
    this.openedCreateMap = true;
    // Añadir los parámetros al formulario: map_configuration, map_sections, map_levels, map_seats
    this.venueForm.addControl(
      'map_configuration',
      new FormControl(null, Validators.required),
    );
    this.venueForm.addControl(
      'map_sections',
      new FormControl(null, Validators.required),
    );
    this.venueForm.addControl(
      'map_levels',
      new FormControl(null, Validators.required),
    );
    this.venueForm.addControl(
      'map_seats',
      new FormControl(null, Validators.required),
    );
    this.venueForm.addControl('map_nav_preview', new FormControl(null));
    this.venueForm.addControl('zone_navigation', new FormControl(false));
    // Remover el requerimiento de capacity cuando se crea un mapa
    this.venueForm.controls['capacity'].clearValidators();
    this.venueForm.controls['capacity'].updateValueAndValidity();
  }
}
